import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { ProphetMetrics, ViewDatesOutput } from '../../../generated/graphql.generated';
import { eventTypeInfoMap } from '../../global.constants';
import { ResponsiveHelperService } from '../../services/responsive-helper.service';
import { ForecastCardComponent } from '../forecast-card/forecast-card.component';
import { PacingCardComponent } from '../pacing-card/pacing-card.component';

@Component({
  selector: 'app-view-dates-card',
  standalone: true,
  imports: [CommonModule, SkeletonModule, TooltipModule, ForecastCardComponent, PacingCardComponent],
  templateUrl: './view-dates-card.component.html',
  styleUrls: ['./view-dates-card.component.scss'],
})
export class ViewDatesCardComponent {
  @Input() data: ViewDatesOutput | null;
  @Input() showForecast = false;
  @Input() showPacing = false;
  @Input() prophetMetrics: ProphetMetrics | null = null;

  constructor(public responsive: ResponsiveHelperService) {}

  getEventTypeColor(type: string): string {
    const backgroundColor = eventTypeInfoMap[type]?.backgroundColor || 'primary-color';
    return `var(--${backgroundColor})`;
  }

  get utilizationPercent(): number {
    if (!this.data) return 0;
    // maxSystemUtilization returns 0-100. Convert to 0-1.
    const maxUtil = this.maxSystemUtilization / 100;
    
    // Round to nearest dashed line (8 lines total)
    const steps = Math.round(maxUtil * 8);
    const percent = (steps / 8) * 100;
    return Math.min(100, percent);
  }

  getUtilizationColor(): string {
    if (!this.data) return 'var(--surface-border)';
    const maxUtil = this.maxSystemUtilization / 100;
    
    const truckCapacity = this.data.truckCapacity || 0;
    const crewCapacity = this.data.crewCapacity || 0;
    const hasCapacity = truckCapacity > 0 || crewCapacity > 0;
    if (!hasCapacity) return 'var(--surface-border)';
    
    const availabilityRatio = Math.max(0, 1 - maxUtil);
    
    let level = 'low';
    if (availabilityRatio > 0.66) level = 'high';
    else if (availabilityRatio > 0.33) level = 'medium';
    
    return level === 'high'
          ? 'var(--blue-500)'
          : level === 'medium'
              ? 'var(--orange-500)'
              : 'var(--red-500)';
  }

  /**
   * Percentage of truck capacity actually used/assigned.
   * Requirement: percent = (actualTrucksUsed / truckCapacity) * 100, capped at 100
   */
  get truckUtilization(): number {
    if (!this.data?.truckCapacity) return 0;
    return Math.min(100, (this.data.actualTrucksUsed / this.data.truckCapacity) * 100);
  }

  get crewRequired(): number {
    if (!this.data) return 0;
    return Math.max(this.data.actualCrewRequired, this.data.actualCrewUsed);
  }

  /**
   * Percentage of crew capacity assigned (people scheduled).
   * Used for the filled part of the crew progress bar.
   * Requirement: percent = (actualCrewUsed / crewCapacity) * 100, capped at 100
   */
  get crewAssignedUtilization(): number {
    if (!this.data?.crewCapacity) return 0;
    return Math.min(100, (this.data.actualCrewUsed / this.data.crewCapacity) * 100);
  }

  /**
   * Percentage of crew capacity required OR assigned (whichever is higher).
   * Represents the peak load or demand on the crew resource.
   * Used for the width of the crew bar to show total demand.
   * Requirement: percent = max(actualCrewUsed, actualCrewRequired) / crewCapacity * 100, capped at 100
   */
  get crewUtilization(): number {
    if (!this.data?.crewCapacity) return 0;
    const req = (this.data as any).actualCrewRequired || 0;
    return Math.min(100, (Math.max(this.data.actualCrewUsed, req) / this.data.crewCapacity) * 100);
  }

  /**
   * The maximum utilization across both resources (Trucks and Crew).
   * Determines the position of the bottleneck marker.
   * Requirement: return the max of truckUtilization and crewUtilization to locate bottleneck
   */
  get maxSystemUtilization(): number {
    return Math.max(this.truckUtilization, this.crewUtilization);
  }

  get actualMovingJobs(): number {
    if (!this.data?.totalEventsByType) return 0;
    const movingType = this.data.totalEventsByType.find(t => t.type.toLowerCase() === 'moving');
    return movingType ? movingType.count : 0;
  }
}
